import React, { useEffect, useState } from 'react';
import { Col, Container, Row, Button, Modal, Spinner } from 'react-bootstrap';
import HeaderAdmin from './HeaderAdmin';
import { firestore } from '../Firebase';

const LoginSessions = ({ embedded }) => {
  const [viewers, setViewers] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: 'lastUpdated',
    direction: 'desc',
  });
  const [showClearModal, setShowClearModal] = useState(false);
  const [isClearingSessions, setIsClearingSessions] = useState(false);

  useEffect(() => {
    // Optimize the viewers listener to be more efficient
    const unsubscribeViewers = firestore
      .collection('viewers')
      .onSnapshot((snapshot) => {
        const viewers = snapshot.docs.map((doc) => {
          const userData = doc.data();

          // Calculate activity status based on timestamp only
          let isActive = false;
          if (userData.timestamp) {
            const secondsSinceUpdate =
              Math.floor(Date.now() / 1000) - userData.timestamp.seconds;
            // Allow 90 seconds (1.5 mins) buffer for network lag before marking inactive
            if (secondsSinceUpdate < 90) {
              isActive = true;
            }
          }

          // Format login time if it exists
          let formattedLoginTime = userData.loginTime;
          let loginTimeTimestamp = 0;
          if (userData.loginTime instanceof Object) {
            loginTimeTimestamp = userData.loginTime.seconds * 1000;
            formattedLoginTime = new Date(loginTimeTimestamp).toLocaleString();
          }

          // Format lastUpdated time if it exists
          let formattedLastUpdated = 'N/A';
          let lastUpdatedTimestamp = 0;
          if (userData.lastUpdated) {
            // Check if lastUpdated is a number or Firestore timestamp object
            if (typeof userData.lastUpdated === 'number') {
              lastUpdatedTimestamp = userData.lastUpdated;
            } else if (
              userData.lastUpdated instanceof Object &&
              userData.lastUpdated.seconds
            ) {
              lastUpdatedTimestamp = userData.lastUpdated.seconds * 1000;
            }

            if (lastUpdatedTimestamp > 0) {
              formattedLastUpdated = new Date(
                lastUpdatedTimestamp
              ).toLocaleString();
            }
          }

          // Return a new object with all original fields plus our calculated fields
          return {
            id: doc.id,
            ...userData, // Keep all original fields
            loginTime: formattedLoginTime,
            loginTimeTimestamp: loginTimeTimestamp,
            formattedLastUpdated: formattedLastUpdated,
            lastUpdatedTimestamp: lastUpdatedTimestamp,
            isActive: isActive,
          };
        });

        setViewers(viewers);
      });

    // Keep the interval for updating activity status of existing users
    const activityInterval = setInterval(() => {
      setViewers((prevViewers) => {
        if (prevViewers.length === 0) return prevViewers;

        return prevViewers.map((viewer) => {
          // Only update the isActive field, preserve all other fields
          let isActive = false;
          if (viewer.timestamp) {
            const secondsSinceUpdate =
              Math.floor(Date.now() / 1000) - viewer.timestamp.seconds;
            // Allow 90 seconds buffer
            isActive = secondsSinceUpdate < 90;
          }
          return { ...viewer, isActive };
        });
      });
    }, 15000);

    return () => {
      unsubscribeViewers();
      clearInterval(activityInterval);
    };
  }, []);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedViewers = React.useMemo(() => {
    let sortableViewers = [...viewers];
    if (sortConfig.key !== null) {
      sortableViewers.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle special cases for sorting
        if (sortConfig.key === 'loginTime') {
          aValue = a.loginTimeTimestamp;
          bValue = b.loginTimeTimestamp;
        } else if (sortConfig.key === 'lastUpdated') {
          aValue = a.lastUpdatedTimestamp;
          bValue = b.lastUpdatedTimestamp;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableViewers;
  }, [viewers, sortConfig]);

  // Function to download table data as CSV
  const downloadCSV = () => {
    // Define the headers for the CSV file
    const headers = [
      'User Name',
      'Login Time',
      'Active',
      'Last Heartbeat',
      'IP Address',
      'Browser',
      'OS',
      'Device',
      'Screen',
      'Referrer',
      'Conn Type',
      'Session ID',
      'User ID',
    ];

    // Create CSV content
    let csvContent = headers.join(',') + '\n';

    // Add data rows
    sortedViewers.forEach((viewer) => {
      const row = [
        viewer.userName || '',
        viewer.loginTime || '',
        viewer.isActive ? 'Yes' : 'No',
        viewer.formattedLastUpdated || '',
        viewer.IP || '',
        viewer.browser || '',
        viewer.os || '',
        viewer.deviceType || '',
        viewer.screenResolution || '',
        viewer.referrer || '',
        viewer.connectionType || '',
        viewer.id || '',
        viewer.userID || '',
      ];

      // Escape commas and quotes in the data
      const escapedRow = row.map((field) => {
        // If field contains commas or quotes, wrap in quotes and escape inner quotes
        if (field.includes(',') || field.includes('"')) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      });

      csvContent += escapedRow.join(',') + '\n';
    });

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Create a link and trigger download
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `user_sessions_${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearLoginSessions = async () => {
    setIsClearingSessions(true);

    try {
      let deletedCount = 0;
      let snapshot;

      do {
        snapshot = await firestore.collection('viewers').limit(500).get();

        if (snapshot.empty) {
          break;
        }

        const batch = firestore.batch();
        snapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
        });

        await batch.commit();
        deletedCount += snapshot.size;
      } while (!snapshot.empty);

      setShowClearModal(false);
      alert(`Cleared ${deletedCount} login session record${deletedCount === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error('Error clearing login sessions:', error);
      alert('Error clearing login sessions: ' + error.message);
    } finally {
      setIsClearingSessions(false);
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    }
    return null;
  };

  const activeCount = viewers.filter(viewer => viewer.isActive).length;

  const content = (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="h3 m1-1 mb-0">Login Sessions: <span style={{fontSize: '1rem', color: '#2ecc71'}}>({activeCount} Live Now)</span></div>
        <div className="d-flex gap-2">
          <Button variant="primary" size="sm" onClick={downloadCSV}>
            <i className="fas fa-download mr-1"></i> Download CSV
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => setShowClearModal(true)}
            disabled={viewers.length === 0 || isClearingSessions}
          >
            Clear Sessions
          </Button>
        </div>
      </div>
      <Modal show={showClearModal} onHide={() => !isClearingSessions && setShowClearModal(false)} centered>
        <Modal.Header closeButton={!isClearingSessions}>
          <Modal.Title>Clear login sessions?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will delete all login session records from the <code>viewers</code> collection.
          </p>
          <p className="mb-0">
            It only clears this session table. It does not delete registered users,
            videos, questions, chats, or other event content. Download the CSV first
            if you need to keep a record.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowClearModal(false)}
            disabled={isClearingSessions}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={clearLoginSessions}
            disabled={isClearingSessions}
          >
            {isClearingSessions ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Clearing...
              </>
            ) : (
              'Delete all session records'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <table className="table table-striped" style={{ fontSize: '0.75rem' }}>
        <thead>
          <tr style={{ lineHeight: '1.2' }}>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('userName')}
            >
              User Name{getSortIcon('userName')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('loginTime')}
            >
              Login Time{getSortIcon('loginTime')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('isActive')}
            >
              Active{getSortIcon('isActive')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('lastUpdated')}
            >
              Last Heartbeat{getSortIcon('lastUpdated')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('IP')}
            >
              IP Address{getSortIcon('IP')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('browser')}
            >
              Browser{getSortIcon('browser')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('os')}
            >
              OS{getSortIcon('os')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('deviceType')}
            >
              Device{getSortIcon('deviceType')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('screenResolution')}
            >
              Screen{getSortIcon('screenResolution')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('referrer')}
            >
              Referrer{getSortIcon('referrer')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('connectionType')}
            >
              Conn{getSortIcon('connectionType')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('sessionID')}
            >
              Session ID{getSortIcon('sessionID')}
            </th>
            <th
              scope="col"
              className="py-1 px-1"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSort('userID')}
            >
              User ID{getSortIcon('userID')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedViewers.map((viewer) => (
            <tr key={viewer.id} style={{ lineHeight: '1.2' }}>
              <td className="py-1 px-1">{viewer.userName}</td>
              <td className="py-1 px-1">{viewer.loginTime}</td>
              <td className="py-1 px-1">
                <span
                  style={{
                    display: 'inline-block',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: viewer.isActive ? '#2ecc71' : '#e74c3c',
                    marginRight: '5px',
                  }}
                />
              </td>
              <td className="py-1 px-1">{viewer.formattedLastUpdated}</td>
              <td className="py-1 px-1">{viewer.IP}</td>
              <td className="py-1 px-1">{viewer.browser}</td>
              <td className="py-1 px-1">{viewer.os}</td>
              <td className="py-1 px-1">{viewer.deviceType}</td>
              <td className="py-1 px-1">{viewer.screenResolution}</td>
              <td
                className="py-1 px-1"
                style={{
                  maxWidth: '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={viewer.referrer}
              >
                {viewer.referrer}
              </td>
              <td className="py-1 px-1">{viewer.connectionType}</td>
              <td className="py-1 px-1">{viewer.sessionID || viewer.id}</td>
              <td className="py-1 px-1">{viewer.userID}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <>
      <Container fluid>
        <Row>
          <HeaderAdmin />
        </Row>

        <Row>
          <Col>
            {content}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default LoginSessions;
